import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(value, currency = 'USD') {
  if (!value) return '-';
  if (value >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`;
  }
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

export function formatDate(date) {
  if (!date) return '-';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return String(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateWithPrecision(value, precision = null) {
  if (!value) return '-';

  if (!precision && typeof value === 'string') {
    if (/^\d{4}$/.test(value)) precision = 'year';
    else if (/^\d{4}-\d{2}$/.test(value)) precision = 'month';
    else precision = 'day';
  }

  if (precision === 'year') {
    return String(value).slice(0, 4);
  }

  if (precision === 'month') {
    const [year, month] = String(value).split('-');
    const parsed = new Date(`${year}-${month}-01T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }

  return formatDate(value);
}

export function formatRoundLabel(roundType) {
  if (!roundType) return '-';
  const normalized = String(roundType).replace(/_/g, ' ').trim().toLowerCase();
  const titleCased = normalized.replace(/\b\w/g, (char) => char.toUpperCase());
  return titleCased
    .replace(/\bIpo\b/g, 'IPO')
    .replace(/\bPre Seed\b/g, 'Pre-Seed')
    .replace(/\bSeries ([A-Z])\b/g, (_, series) => `Series ${series}`);
}

export function formatConfidencePercent(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return `${Math.round(numeric * 100)}%`;
}

export function formatRelativeTime(date) {
  if (!date) return '-';
  const d = new Date(date);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(date);
}

export function getStageColor(stage) {
  switch (stage?.toLowerCase()) {
    case 'early':
    case 'seed':
    case 'pre-seed':
      return 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20';
    case 'growth':
    case 'series_a':
    case 'series_b':
      return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
    case 'late':
    case 'series_c':
    case 'series_d':
      return 'text-amber-400 bg-amber-400/10 border-amber-400/20';
    default:
      return 'text-zinc-400 bg-zinc-400/10 border-zinc-400/20';
  }
}

export function getScoreColor(score) {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-blue-400';
  if (score >= 40) return 'text-amber-400';
  return 'text-rose-400';
}

export function truncate(str, length = 100) {
  if (!str) return '';
  if (str.length <= length) return str;
  return str.substring(0, length) + '...';
}

export function readSessionJson(key, fallback = null) {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return fallback;
  }
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
}

export function writeSessionJson(key, value) {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore storage quota and private mode failures.
  }
}
